import { Button, TableItem } from 'components/atoms';
import { FormInput, MagellanPopups } from 'components/molecules';
import LabelReportButtonGenerate from 'components/molecules/buttons/reportGenerate/LabelReportButtonGenerate';
import LabelReportButtonDownload from 'components/molecules/buttons/reportDownload/LabelReportButtonDownload';
import SendButtonEmail from 'components/molecules/buttons/sendEmail/SendButtonEmail';
import LabeledEmailSend from 'components/molecules/labeledStatus/LabeledEmailSend';
import LabeledReportStatus from 'components/molecules/labeledStatus/LabeledReportStatus';
import { FooterTable, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useFormikContext } from 'formik';
import { useHasPermission } from 'hooks';
import { useInfiniteTable } from 'hooks/useInfiniteTable';
import { useCustomParamId } from 'hooks/useParamId';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useStreamFileDownload } from 'hooks/useStreamFileDownload';
import { labelReportIndividualValidator } from 'pages/private/label-reports/list/LabelReports';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiClock, PiClockCounterClockwiseBold, PiNavigationArrow } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { PopupActions } from 'reactjs-popup/dist/types';
import { DOCUMENT_TYPE } from 'services/email/types';
import {
  useCancelLabelPeriodReport,
  useCheckLabelReportPeriodsMutation,
  useDeleteReport,
  useGenerateLabelReportDetailedFile,
  useGenerateLabelReportFile,
  useGetLabelReportFile,
  useGetLabelReportsQueue,
  useGetLabelReportsStatisticsByLabel,
  useGetReportsByLabelInfinite,
  usePostLabelPeriodReport,
} from 'services/label-report';
import { ILabelReportsByLabelParams } from 'services/label-report/types';
import { ILabelForm } from 'services/labels/types';
import { useGetSettlementLightList } from 'services/settlementPeriods';
import { EMAIL_SEND } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatCurrency } from 'utils/global/currency.utils';
import { formatDate, formatDateToInput } from 'utils/global/date.utils';
import { getSettlementPeriodName } from 'utils/global/name.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { object } from 'yup';

export const labelReportDetailValidator = object().shape({
  generate: labelReportIndividualValidator,
});

const LabelReports = () => {
  const labelId = useCustomParamId('id', '/label');
  const hasPermission = useHasPermission();

  usePermissionGuard('LabelReportOrderPermissionsLIST**');

  const { t } = useTranslation();

  const navigate = useNavigate();

  const { values, setFieldValue } = useFormikContext<ILabelForm>();

  const reportsSearch: ILabelReportsByLabelParams = {
    startSettlementPeriodId:
      values.reports?.search?.startSettlementPeriodId ?? '',
    endSettlementPeriodId: values.reports?.search?.endSettlementPeriodId ?? '',
  };

  const { setFile } = useStreamFileDownload(useGetLabelReportFile);

  const labelReports = useGetReportsByLabelInfinite(labelId, reportsSearch);

  const tableParams = useInfiniteTable(labelReports, {
    selectable: false,
  });

  const generateLabelReportFile = useGenerateLabelReportFile();
  const generateLabelReportDetailedFile = useGenerateLabelReportDetailedFile();

  const removeReport = useDeleteReport();

  const reportsStatistics = useGetLabelReportsStatisticsByLabel(
    labelId,
    reportsSearch
  );

  const settlementPeriods = useGetSettlementLightList({ controlApplicationSettings: true });

  const createLabelReport = usePostLabelPeriodReport();
  const cancelLabelReport = useCancelLabelPeriodReport();

  const reportsOrder = useGetLabelReportsQueue(labelId);

  const settlementPeriodOptions = settlementPeriods.data?.flat().map(s => ({
    label: getSettlementPeriodName(s.settlementPeriodName),
    value: s.id,
  }));

  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [warningReason, setWarningReason] = useState<
    'overlapping' | 'alreadySettled' | null
  >(null);
  const [reportData, setReportData] = useState<{
    labelId: string;
    startSettlementPeriodId: string;
    endSettlementPeriodId: string;
    planedStartDate: string;
    asap: boolean;
  } | null>(null);

  const checkPeriods = useCheckLabelReportPeriodsMutation();

  const popupRef = useRef<PopupActions>(null);

  const onRemoveReport = async (actions: PopupActions, id: string) => {
    try {
      await removeReport.mutateAsync(id);
      await labelReports.refetch();
      actions.close();
    } catch (error) {
      if (error instanceof Error) {
        onErrorShowAllErrorsToast(error);
      } else {
        onErrorShowAllErrorsToast(new Error('Unknown error occurred'));
      }
    }
  };

  useEffect(() => {
    if (showWarningPopup && popupRef.current) {
      popupRef.current.open();
    }
  }, [showWarningPopup]);

  const onGenerateLabelReport = async (actions: PopupActions) => {
    const reportData = {
      labelId: labelId,
      startSettlementPeriodId:
        values.reports?.generate?.startSettlementPeriodId ?? '',
      endSettlementPeriodId:
        values.reports?.generate?.endSettlementPeriodId ?? '',
      planedStartDate: values.reports?.generate?.planedStartDate ?? '',
      asap: values.reports?.generate?.asap ?? false,
    };

    try {
      const checkResult = await checkPeriods.mutateAsync({
        labelId,
        startSettlementPeriodId:
          values.reports?.generate?.startSettlementPeriodId ?? '',
        endSettlementPeriodId:
          values.reports?.generate?.endSettlementPeriodId ?? '',
      });

      if (
        checkResult.hasOverlappingPeriods ||
        checkResult.isAlreadySettled
      ) {
        setReportData(reportData);
        setWarningReason(
          checkResult.hasOverlappingPeriods ? 'overlapping' : 'alreadySettled',
        );
        setShowWarningPopup(true);
        actions.close();
        return;
      }

      const createResult = await createLabelReport.mutateAsync(reportData);
      actions.close();
      await reportsOrder.refetch();
    } catch (error) {
      if (error instanceof Error) {
        onErrorShowAllErrorsToast(error);
      } else {
        onErrorShowAllErrorsToast(new Error('Unknown error occurred'));
      }
    }
  };

  const allAmountLength = formatCurrency(
    reportsStatistics?.data?.allAmountEarned
  ).length;

  const allAmountLabelLength = formatCurrency(
    reportsStatistics?.data?.allAmountForLabel
  ).length;
  const allAmountOwnerLength = formatCurrency(
    reportsStatistics?.data?.allAamountForOwner
  ).length;

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval>;

    const fetchData = async () => {
      await Promise.all([reportsOrder.refetch(), labelReports.refetch()]);
    };

    if (
      reportsOrder?.data?.length !== undefined &&
      reportsOrder?.data?.length > 0
    ) {
      intervalId = setInterval(fetchData, 15000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [reportsOrder?.data?.length]);

  const handleCreateReport = async () => {
    if (reportData) {
      try {
        const createResult = await createLabelReport.mutateAsync(reportData);
        setShowWarningPopup(false);
        setWarningReason(null);
        await reportsOrder.refetch();

        // Czyszczenie danych formularza
        setFieldValue('reports.generate', {
          startSettlementPeriodId: '',
          endSettlementPeriodId: '',
          planedStartDate: formatDateToInput(new Date(), true),
          asap: true,
        });
      } catch (createError) {
        if (createError instanceof Error) {
          onErrorShowAllErrorsToast(createError);
        } else {
          onErrorShowAllErrorsToast(
            new Error('Unknown error occurred while creating report')
          );
        }
      }
    }
  };

  const onGenerateLabelReportFile = async (labelReportId: string) =>
    await generateLabelReportFile.mutateAsync(
      { labelReportId },
      {
        onSuccess: async () => await labelReports.refetch(),
        onError: error => onErrorShowAllErrorsToast(error),
      }
    );

  const onGenerateLabelReportDetailedFile = async (labelReportId: string) =>
    await generateLabelReportDetailedFile.mutateAsync(
      { labelReportId },
      {
        onSuccess: async () => await labelReports.refetch(),
        onError: error => onErrorShowAllErrorsToast(error),
      }
    );

  const goToReport = (id: string) =>
    navigate(`/label/${labelId}/reports/${id}`);

  return (
    <TopbarPage
      topbar={
        <TopBar
          size='compact'
          useHelmet={false}
          hideHeader
          customBackItem={<div className='none' />}
          // title={'filters'}
          topbarContent={
            <div className='flex w-full justify-between overflow-hidden'>
              <div className='flex gap-2 w-[500px]'>
                <FormInput.Select
                  name='reports.search.startSettlementPeriodId'
                  label={t('input.label.startSettlementPeriod')}
                  options={settlementPeriodOptions}
                  placeholder={''}
                  displayMode={false}
                />
                <FormInput.Select
                  name='reports.search.endSettlementPeriodId'
                  label={t('input.label.endSettlementPeriod')}
                  options={settlementPeriodOptions}
                  placeholder={''}
                  displayMode={false}
                />
              </div>
              <div className='flex gap-2'>
                <Button onlyIcon isClear onClick={() => navigate('history')}>
                                    <Button.Description description={t('history.contract')} />

                                    <Button.Icon icon={<PiClockCounterClockwiseBold />} />
                </Button>

                {hasPermission('LabelReportPermissionsLIST**', 1) && (
                  <MagellanPopups.PopupChannelCreateForm
                    onAdd={onGenerateLabelReport}
                    clearPopupValues={() =>
                      setFieldValue('reports.generate', {
                        startSettlementPeriodId: '',
                        endSettlementPeriodId: '',
                        planedStartDate: formatDateToInput(new Date(), true),
                        asap: true,
                      })
                    }
                    trigger={
                      <Button variant={BUTTON_VARIANT.submit}>
                        <Button.Text text={t('button.generate')} />
                      </Button>
                    }
                    header={t('label-reports.list.popup.header')}>
                    <FormInput.Select
                      label={t('input.label.startSettlementPeriod')}
                      name='reports.generate.startSettlementPeriodId'
                      isAsync
                      options={settlementPeriodOptions}
                      displayMode={false}
                    />
                    <FormInput.Select
                      label={t('input.label.endSettlementPeriod')}
                      name='reports.generate.endSettlementPeriodId'
                      isAsync
                      options={settlementPeriodOptions}
                      displayMode={false}
                    />
                    <FormInput.Select
                      displayMode={false}
                      name={`reports.generate.asap`}
                      label={t('input.label.asap')}
                      options={[
                        { label: t('input.option.yes'), value: true },
                        { label: t('input.option.no'), value: false },
                      ]}
                    />
                    <FormInput.Text
                      label={t('input.label.plannedStartDate')}
                      type='datetime-local'
                      min={formatDateToInput(new Date(), true)}
                      name='reports.generate.planedStartDate'
                      disabled={values.reports?.generate?.asap}
                      displayMode={false}
                    />
                  </MagellanPopups.PopupChannelCreateForm>
                )}
              </div>
            </div>
          }
        />
      }>
      <div className='flex flex-col w-full gap-4 h-full justify-between'>
        {!!reportsOrder.data?.length && (
          <div className='flex w-full max-h-[30%]'>
            <Table
              id={'LabelReports-order-label'}
              size='compact'
              data={{ nodes: reportsOrder.data }}
              columns={[
                {
                  label: t('input.label.label'),
                  renderCell: ({ labelName }) => (
                    <TableItem>
                      <TableItem.Text text={labelName} />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t('input.label.startSettlementPeriod'),
                  renderCell: ({ startSettlementPeriodName }) => (
                    <TableItem>
                      {startSettlementPeriodName.name && (
                        <TableItem.Text
                          text={getSettlementPeriodName(
                            startSettlementPeriodName
                          )}
                        />
                      )}
                    </TableItem>
                  ),
                  resize: true,
                },

                {
                  label: t('input.label.endSettlementPeriod'),
                  renderCell: ({ endSettlementPeriodName }) => (
                    <TableItem>
                      {endSettlementPeriodName.name && (
                        <TableItem.Text
                          text={getSettlementPeriodName(
                            endSettlementPeriodName
                          )}
                        />
                      )}
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t('input.label.plannedStartDate'),
                  renderCell: ({ plannedStartDate }) => (
                    <TableItem>
                      <TableItem.Text
                        text={formatDate(plannedStartDate, true)}
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t('input.label.status'),
                  renderCell: ({ reportGenerationStatusId }) => (
                    <TableItem>
                      <LabeledReportStatus status={reportGenerationStatusId} />
                    </TableItem>
                  ),
                },
              ]}
            />
          </div>
        )}
        <div
          className={`flex w-full ${
            !!reportsOrder.data?.length ? 'h-[70%]' : 'h-full'
          }`}>
          <FooterTable
            id={'LabelReports-allForOwner4'}
            {...tableParams}
            size='compact'
            columns={[
              {
                label: t('input.label.startSettlementPeriod'),
                renderCell: ({ startSettlementPeriodName }) => (
                  <TableItem>
                    <TableItem.Text
                      text={getSettlementPeriodName(startSettlementPeriodName)}
                    />
                  </TableItem>
                ),
                resize: true,
                footer: t('table.total'),
              },
              {
                label: t('input.label.endSettlementPeriod'),
                renderCell: ({ endSettlementPeriodName }) => (
                  <TableItem>
                    <TableItem.Text
                      text={getSettlementPeriodName(endSettlementPeriodName)}
                    />
                  </TableItem>
                ),
                resize: true,
              },
              {
                label: t('input.label.number'),
                renderCell: ({ labelReportNumber }) => (
                  <TableItem>
                    <TableItem.Text text={labelReportNumber ?? undefined} />
                  </TableItem>
                ),
                resize: true,
              },
              {
                label: t('input.label.amountEarned'),
                renderCell: ({ amountEarned }) => (
                  <TableItem>
                    <TableItem.Currency
                      text={formatCurrency(amountEarned, {
                        length: allAmountLength,
                      })}
                    />
                  </TableItem>
                ),
                resize: true,
                footer: formatCurrency(
                  reportsStatistics.data?.allAmountEarned,
                  { length: allAmountLength }
                ),
              },
              {
                label: t('input.label.amountForLabel'),
                renderCell: ({ amountForLabel }) => (
                  <TableItem>
                    <TableItem.Currency
                      text={formatCurrency(amountForLabel, {
                        length: allAmountLabelLength,
                      })}
                    />
                  </TableItem>
                ),
                resize: true,
                footer: formatCurrency(
                  reportsStatistics.data?.allAmountForLabel,
                  { length: allAmountLabelLength }
                ),
              },
              {
                label: t('input.label.amountForOwner'),
                renderCell: ({ amountForOwner }) => (
                  <TableItem>
                    <TableItem.Currency
                      text={formatCurrency(amountForOwner, {
                        length: allAmountOwnerLength,
                      })}
                    />
                  </TableItem>
                ),
                resize: true,
                footer: formatCurrency(
                  reportsStatistics.data?.allAmountForOwner,
                  { length: allAmountOwnerLength }
                ),
              },
              {
                label: t('label.table.compliance'),
                renderCell: ({ isOutdated }) => (
                  <TableItem>
                    {isOutdated && (
                      <TableItem.Label color='orange' size='compact'>
                        <PiClock />
                        {t('label.compliance.outdated')}
                      </TableItem.Label>
                    )}
                  </TableItem>
                ),
                resize: true,
              },
              {
                label: t('input.label.email-sender'),
                renderCell: ({ id, sendStatus }) => (
                  <TableItem>
                    <div className='flex items-center gap-2'>
                      {sendStatus !== EMAIL_SEND.notSend && (
                        <LabeledEmailSend status={sendStatus} />
                      )}
                      {sendStatus !== EMAIL_SEND.planned && (
                        <SendButtonEmail
                          documentId={id}
                          documentType={DOCUMENT_TYPE.labelReport}
                          refetch={labelReports.refetch}
                          status={sendStatus}
                        />
                      )}
                    </div>
                  </TableItem>
                ),
                resize: true,
              },
              {
                label: '',
                renderCell: ({
                  id,
                  filePath,
                  labelReportNumber,
                  canGenerateDetailedReport,
                  detailedFilePath,
                }) => (
                  <TableItem justifyPosition='end'>
                    <div className='flex gap-1'>
                      <LabelReportButtonGenerate
                        id={id}
                        alignTop={
                          (labelReports.data?.pages.flat().reverse()[0])?.id === id
                        }
                        canGenerateDetailedReport={canGenerateDetailedReport}
                        onGenerateSimple={onGenerateLabelReportFile}
                        onGenerateDetailed={onGenerateLabelReportDetailedFile}
                        isLoadingSimple={
                          generateLabelReportFile.isPending &&
                          generateLabelReportFile.variables.labelReportId === id
                        }
                        isLoadingDetailed={
                          generateLabelReportDetailedFile.isPending &&
                          generateLabelReportDetailedFile.variables.labelReportId ===
                            id
                        }
                      />

                      <LabelReportButtonDownload
                        id={id}
                        name={labelReportNumber ?? ''}
                        alignTop={
                          (labelReports.data?.pages.flat().reverse()[0])?.id === id
                        }
                        canGenerateDetailedReport={!!detailedFilePath}
                      />

                      

                      {/* <MagellanPopups.FilePreviewPopup
                        fileId={id}
                        fileName={labelReportNumber ?? filePath ?? ''}
                        fileService={useGetLabelReportFile}
                        trigger={
                          <Button
                            type='button'
                            isClear
                            onlyIcon
                            disabled={!filePath}>
                                                        <Button.Description description={t('button.download')} />

                                                        <Button.Icon icon={<PiDownload />} />
                          </Button>
                        }
                        onDownload={() =>
                          setFile({
                            id,
                            name: labelReportNumber ?? filePath ?? '',
                          })
                        }
                      /> */}
                      <MagellanPopups.DeleteWarning
                        name={t('label-reports.what')}
                        onRemove={actions => onRemoveReport(actions, id)}
                      />
                      <Button
                        type='button'
                        isClear
                        onlyIcon
                        onClick={() => goToReport(id)}>
                                                <Button.Description description={t('button.open')} />

                                                <Button.Icon
                          icon={<PiNavigationArrow transform='scale(-1,1)' />}
                        />
                      </Button>
                    </div>
                  </TableItem>
                ),
              },
            ]}
          />
        </div>
      </div>

      <MagellanPopups.CriticalWarningPopup
        open={showWarningPopup}
        onClose={() => {
          setShowWarningPopup(false);
          setWarningReason(null);
        }}
        onConfirm={handleCreateReport}
        title={t('label-reports.warning.title')}
        message={
          warningReason === 'alreadySettled'
            ? t('label-reports.warning.alreadySettledMessage')
            : t('label-reports.warning.message')
        }
        confirmButtonText={t('popup.generate')}
        cancelButtonText={t('popup.resign')}
      />
    </TopbarPage>
  );
};

export default LabelReports;
